/* ────────────────────────────────────────────────────────────────
   Consent + tracker loading (CIPA / CCPA risk mitigation)
   ────────────────────────────────────────────────────────────────
   Single source of truth for the site's consent state and for
   loading EVERY third-party tracker. Nothing that phones home
   (GTM → GA4 / Google Ads, or CallRail's swap.js) is allowed to
   load until the visitor clicks Accept on the consent banner —
   or returns with a prior Accept cookie.

   Why: the wave of CIPA §638.51 "pen register" demand letters
   targets sites whose trackers fire before consent. Consent is
   the defense, so the gate has to be REAL — no script tags in
   HTML, no plugin-injected GTM snippet. See ConsentBanner
   (consent-banner.js) for the UI and gatsby-browser.js for the
   returning-visitor path. Ported from the GVD Renovations
   implementation (July 2026); see the CA privacy hardening
   playbook.

   Consent model (accept-only):
   - Accept  → first-party cookie `webperfex_consent=granted`
               (12 mo) + trackers load immediately.
   - X close → sessionStorage flag only; banner stays hidden for
               this browsing session, re-appears next visit, and
               trackers NEVER load without an Accept.
   - Global Privacy Control (navigator.globalPrivacyControl) →
               treated as an opt-out: no banner, no trackers.

   UNLIKE GVD: CallRail IS consent-gated here (decision, Igor,
   2026-07). WebPerfex displays a single company number — the
   CallRail swap is source/keyword attribution, not the
   market-specific local-number display that justified GVD's
   ungated carve-out. Gating it removes the last pre-consent
   tracker on the site.

   Cloudflare Turnstile (form anti-bot) is NOT gated — it is a
   security control, not an ad/analytics tracker, and forms
   don't work without it. It stays in gatsby-ssr.js.

   Google Consent Mode v2: gatsby-ssr.js sets all consent flags
   to 'denied' as the page-level default (before any Google tag
   could exist). grantConsent() flips them to 'granted' right
   before GTM is injected, so tags boot with the correct state. */

export const CONSENT_COOKIE = 'webperfex_consent'
const DISMISS_KEY = 'webperfex_consent_dismissed'
const GTM_ID = 'GTM-TV4PDB7'
const CALLRAIL_SRC =
  'https://cdn.callrail.com/companies/443611976/055b3434580c4304044d/12/swap.js'

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined'

/* GPC is a browser-level "do not sell/share" signal (CPPA expects
   businesses in scope to honor it). Honoring it costs us nothing
   and reads well in the privacy policy. */
export const hasGPC = () =>
  isBrowser() && navigator.globalPrivacyControl === true

export const getConsent = () => {
  if (!isBrowser()) return null
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + CONSENT_COOKIE + '=([^;]+)')
  )
  return match ? match[1] : null
}

export const isDismissedThisSession = () => {
  if (!isBrowser()) return false
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch (e) {
    return false
  }
}

export const dismissForSession = () => {
  if (!isBrowser()) return
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1')
  } catch (e) {
    /* Safari private mode can throw — banner will just re-show. */
  }
}

/* ── Tracker loaders ──────────────────────────────────────────── */

let trackersLoaded = false

const injectGTM = () => {
  /* Replaces gatsby-plugin-google-tagmanager (removed from
     gatsby-config.js). The old config had includeInDevelopment:
     true, so no NODE_ENV gate here — GTM loads in `gatsby
     develop` too, same as before (after Accept).
     Note: the plugin's enableWebVitalsTracking option is NOT
     replicated — core-web-vitals dataLayer events stop. */
  if (document.getElementById('__gtm_loader')) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  })

  const s = document.createElement('script')
  s.id = '__gtm_loader'
  s.async = true
  s.src = 'https://www.googletagmanager.com/gtm.js?id=' + GTM_ID
  document.head.appendChild(s)
}

const injectCallRail = () => {
  /* Was a hardcoded <script> at the end of <body> in src/html.js;
     now injected only after consent. swap() re-runs on route
     changes via gatsby-browser.js onRouteUpdate. */
  if (document.getElementById('__callrail_swap')) return

  const s = document.createElement('script')
  s.id = '__callrail_swap'
  s.type = 'text/javascript'
  s.async = true
  s.src = CALLRAIL_SRC
  s.onload = function () {
    try {
      if (typeof window.CallTrk !== 'undefined') {
        window.CallTrk.swap()
      }
    } catch (e) {
      console.log('phone swap blocked')
    }
  }
  document.body.appendChild(s)
}

/* Load ALL consent-gated trackers (GTM container + CallRail).
   Called on Accept, and on page load for returning visitors who
   already accepted (gatsby-browser.js onInitialClientRender —
   i.e. after React hydration, so the CallRail number swap never
   races the hydration pass). Idempotent. */
export const loadTrackers = () => {
  if (!isBrowser() || trackersLoaded) return
  trackersLoaded = true

  /* Consent Mode v2: flip the page-level 'denied' defaults (set in
     gatsby-ssr.js) to granted BEFORE the GTM container boots. */
  window.dataLayer = window.dataLayer || []
  function gtag() {
    window.dataLayer.push(arguments)
  }
  gtag('consent', 'update', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  })

  injectGTM()
  injectCallRail()
}

/* Persist Accept and start tracking. */
export const grantConsent = () => {
  if (!isBrowser()) return
  const maxAge = 60 * 60 * 24 * 365 // 12 months, CPRA's re-ask ceiling
  document.cookie =
    CONSENT_COOKIE +
    '=granted; path=/; max-age=' +
    maxAge +
    '; SameSite=Lax; Secure'
  loadTrackers()
}
